import { defaultPageSizeOptions } from '@liveauctioneers/caterwaul-components/lib/Pagination/components/DefaultPageSizeOptions';
import { Tabs } from '@liveauctioneers/hammer-ui-core/tabs';
import { useCallback, useEffect, useState } from 'react';

import type { PaginationFilter } from '@/components/Pagination/hammer-pagination/types/PaginationFilter';

import { CompleteAccountBanner } from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import { RouteButton } from '@/components/RouteButton/RouteButton';
import { DEFAULT_SIMILAR_ITEM_ALERTS_SORT, similarItemAlertsSortOrders } from '@/enums';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    fetchSavedSearchesIfNeeded,
    fetchSavedSearchItemsIfNeeded,
    getSavedSearchesRecentFilters,
    getSavedSearchFilters,
    onSavedSearchFilterChange,
    onSavedSearchRecentFilterChange,
} from '@/redux/modules/savedSearch';
import { fetchSimilarItemAlerts } from '@/redux/modules/similarItemAlerts/similarItemAlerts.actions';
import {
    DEFAULT_SIMILAR_ITEM_ALERTS_PAGE_SIZE,
    type SimilarItemAlertsSort,
} from '@/redux/modules/similarItemAlerts/similarItemAlerts.types';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getFollowedSearches, getFollowedSellersForYouUrl, getForYouUrl, getSimilarItemAlertsUrl } from '@/utils/urls';

import FollowedSellersPageContainer from '../../FollowedSellers/FollowedSellersPageContainer';
import ForYouPageHeaderMeta from '../ForYouPageHeaderMeta/ForYouPageHeaderMeta';
import { ManageSavedSearches } from './components/MySavedSearch/ManageSavedSearches';
import { MySavedSearchPageRecentItems } from './components/MySavedSearch/MySavedSearchPageRecentItems';
import { MySimilarItemAlerts } from './components/MySimilarItemAlerts/MySimilarItemAlerts';
import { PageTitle } from './components/PageTitle/PageTitle';

export type ForYouPageProps = {
    baseUrl: string;
    page: number;
    /** Raw `pageSize` query param — only the Similar Item Alerts tab reads it from the URL. */
    pageSize?: string;
    pathname: string;
    pushNewUrl: (url: string) => void;
    sort: string;
    tab: string;
    terms: string;
};

const ForYouPage = ({ baseUrl, page, pageSize, pathname, pushNewUrl, sort, tab, terms }: ForYouPageProps) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const whiteLabel = useAppSelector(getIsWhiteLabel);
    const isMobileView = useIsNarrowerThan(769);
    const [hasLoaded, setHasLoaded] = useState(false);
    const searchItemsFilters = useAppSelector(getSavedSearchesRecentFilters);
    const searchFilters = useAppSelector(getSavedSearchFilters);

    const onSearchItemsFilterChange = (searchItemsFilters: PaginationFilter) => {
        dispatch(onSavedSearchRecentFilterChange(searchItemsFilters));
    };

    const onSearchesFilterChange = (searchFilters: PaginationFilter) => {
        dispatch(onSavedSearchFilterChange(searchFilters));
    };

    // The tab's controls own the URL (`shouldModifyQueryParams`), which is what this page reads
    // back, so there is no separate filter state to keep. Both values are validated here rather
    // than trusted: an unknown sort makes savedsearch-api fall back to `savedNewest` silently, and
    // an arbitrary pageSize would go straight to the API.
    const similarItemAlertsSort: SimilarItemAlertsSort =
        sort in similarItemAlertsSortOrders ? (sort as SimilarItemAlertsSort) : DEFAULT_SIMILAR_ITEM_ALERTS_SORT;
    const similarItemAlertsPageSize = defaultPageSizeOptions.includes(Number(pageSize))
        ? Number(pageSize)
        : DEFAULT_SIMILAR_ITEM_ALERTS_PAGE_SIZE;
    const similarItemAlertsFilters: PaginationFilter = {
        keyword: terms,
        page: page || 1,
        pageSize: similarItemAlertsPageSize,
        sort: similarItemAlertsSort,
    };

    useEffect(() => {
        if (tab === 'similar-item-alerts') {
            dispatch(
                fetchSimilarItemAlerts({
                    keyword: terms,
                    page: page || 1,
                    pageSize: similarItemAlertsPageSize,
                    sort: similarItemAlertsSort,
                })
            );
        }
    }, [dispatch, page, similarItemAlertsPageSize, similarItemAlertsSort, tab, terms]);

    useEffect(() => {
        if (tab === 'for-you') {
            dispatch(fetchSavedSearchItemsIfNeeded(true));
            if (!hasLoaded) {
                setHasLoaded(true);
            }
        }
    }, [dispatch, tab, page, searchItemsFilters.pageSize, searchItemsFilters.sort, terms, hasLoaded]);

    useEffect(() => {
        if (tab === 'followed-searches') {
            dispatch(
                fetchSavedSearchesIfNeeded(
                    true,
                    page,
                    Number(searchFilters.pageSize),
                    searchFilters.sort,
                    searchFilters.keyword
                )
            );
        }
    }, [dispatch, tab, page, searchFilters.keyword, searchFilters.pageSize, searchFilters.sort, terms]);

    const onTabChange = useCallback(
        (name: string) => {
            if (name === 'for-you') {
                pushNewUrl(getForYouUrl());
                dispatch(fetchSavedSearchItemsIfNeeded(true));
            } else if (name === 'followed-auctioneers-for-you') {
                pushNewUrl(getFollowedSellersForYouUrl());
            } else if (name === 'followed-searches') {
                pushNewUrl(getFollowedSearches());
                dispatch(fetchSavedSearchesIfNeeded(true));
            } else if (name === 'similar-item-alerts') {
                pushNewUrl(getSimilarItemAlertsUrl());
            }
        },
        [dispatch, pushNewUrl]
    );

    const titleCallToAction =
        tab === 'followed-searches' && !isMobileView ? (
            <RouteButton
                aria-label="Follow More Categories"
                className="mt-2xs"
                size="sm"
                to="/c/"
            >
                {formatMessage(['forYou', 'followMoreCategories'])}
            </RouteButton>
        ) : null;

    return (
        <div className="max-[560px]:!mb-2xl max-[560px]:!mx-0 relative mx-auto mb-3xl flex w-full max-w-[1240px] flex-col self-center px-sm">
            <ForYouPageHeaderMeta
                baseUrl={baseUrl}
                tab={tab}
            />
            <CompleteAccountBanner />
            <PageTitle
                callToAction={titleCallToAction}
                className="!my-lg"
                title={formatMessage(['forYou', 'title'])}
            />
            {!whiteLabel ? (
                <Tabs
                    currentTab={pathname && pathname.split('/')[2]}
                    onTabChanged={onTabChange}
                    tabs={[
                        {
                            content: (
                                <MySavedSearchPageRecentItems
                                    filters={{
                                        ...searchItemsFilters,
                                        page: page || 1,
                                        tab: 'items',
                                    }}
                                    onSavedSearchRecentFilterChange={onSearchItemsFilterChange}
                                />
                            ),
                            contentId: 'for-you',
                            label: formatMessage(['forYou', 'items']),
                        },
                        {
                            content: <MySimilarItemAlerts filters={similarItemAlertsFilters} />,
                            contentId: 'similar-item-alerts',
                            label: formatMessage(['forYou', 'similarItemAlerts']),
                        },
                        {
                            content: (
                                <ManageSavedSearches
                                    filters={{
                                        ...searchFilters,
                                        page: page,
                                        tab: 'searches',
                                    }}
                                    onSavedSearchRecentFilterChange={onSearchesFilterChange}
                                />
                            ),
                            contentId: 'followed-searches',
                            label: formatMessage(['forYou', 'searches']),
                        },
                        {
                            content: <FollowedSellersPageContainer tab={tab} />,
                            contentId: 'followed-auctioneers-for-you',
                            label: formatMessage(['forYou', 'auctioneers']),
                        },
                    ]}
                />
            ) : null}
        </div>
    );
};

export default ForYouPage;
